import moment from "moment";
import { DATE_PICKER_MODES, DATE_PICKER_RANGE_TITLES, DATE_PICKER_COMPARE_TITLES, CUSTOM_COMPARE_FUNCTION, getDatePickerRangeCompare } from "@/utils/datepicker";
export const DATEPICKER_SLOTS = {
  RANGE_START: "range-start",
  RANGE_END: "range-end",
  COMPARE_START: "compare-start",
  COMPARE_END: "compare-end"
};
class DatePickerPeriod {
  constructor(start = null, end = null) {
    this.start = start ? moment(start) : null;
    this.end = end ? moment(end) : null;
  }
  get isEmpty() {
    return !this.start && !this.end;
  }
  get isComplete() {
    return !!(this.start && this.end);
  }
  reset() {
    this.start = null;
    this.end = null;
  }
  update({
    start,
    end
  }) {
    this.start = start;
    this.end = end;
  }
  applyClick(date) {
    const mDate = moment(date);
    if (!this.start) {
      this.start = mDate;
      this.end = null;
      return "end";
    }
    if (!this.end) {
      if (mDate.isBefore(this.start, "day")) {
        const oldStart = moment(this.start);
        this.start = mDate;
        this.end = oldStart;
      } else {
        this.end = mDate;
      }
      return "start"; // cycle complete
    }
    this.reset();
    this.start = mDate;
    return "end";
  }
  toObject() {
    return {
      start: this.start,
      end: this.end
    };
  }
}
export class DatePickerSelectionEngine {
  constructor() {
    this.activeSlot = DATEPICKER_SLOTS.RANGE_START;
  }
  advanceSlot(compareType) {
    switch (this.activeSlot) {
      case DATEPICKER_SLOTS.RANGE_START:
        this.activeSlot = DATEPICKER_SLOTS.RANGE_END;
        break;
      case DATEPICKER_SLOTS.RANGE_END:
        // If compare is not custom, skip to next range since compare is auto-filled
        if (compareType === DATE_PICKER_COMPARE_TITLES.CUSTOM) {
          this.activeSlot = DATEPICKER_SLOTS.COMPARE_START;
        } else {
          this.activeSlot = DATEPICKER_SLOTS.RANGE_START;
        }
        break;
      case DATEPICKER_SLOTS.COMPARE_START:
        this.activeSlot = DATEPICKER_SLOTS.COMPARE_END;
        break;
      case DATEPICKER_SLOTS.COMPARE_END:
        this.activeSlot = DATEPICKER_SLOTS.RANGE_START;
        break;
    }
  }
  setSlot(slot) {
    if (Object.values(DATEPICKER_SLOTS).includes(slot) || slot === null) {
      this.activeSlot = slot;
    }
  }
}
export class DatePickerEngine {
  constructor({
    weekType,
    selectedPeriod,
    rangeType = DATE_PICKER_RANGE_TITLES.CURRENT_WEEK,
    compareType = DATE_PICKER_COMPARE_TITLES.PRECEDING_PERIOD
  }) {
    this.weekType = weekType;
    this.selectedPeriod = selectedPeriod;
    this.rangeType = rangeType;
    this.compareType = compareType;
    this.range = new DatePickerPeriod();
    this.compare = new DatePickerPeriod();
    this.selection = new DatePickerSelectionEngine();
    this._initializeState();
  }
  _initializeState() {
    if (this.rangeType === DATE_PICKER_RANGE_TITLES.CUSTOM) {
      this.DATE_PICKER_MODE = DATE_PICKER_MODES.CUSTOM_MODE;
      // this.rangeSelection = "start";
    } else {
      this.selectRangeType(this.rangeType);
    }
  }

  // -----------------------
  get activeSlot() {
    return this.selection.activeSlot;
  }
  advanceSlot() {
    this.selection.advanceSlot(this.compareType);
  }
  setSlot(slot) {
    this.selection.setSlot(slot);
  }
  getRangePosition() {
    const slot = this.activeSlot;
    if (slot === DATEPICKER_SLOTS.RANGE_START) return "start";
    if (slot === DATEPICKER_SLOTS.RANGE_END) return "end";
    return null;
  }
  setRangePosition(position) {
    if (position === "start") {
      this.setSlot(DATEPICKER_SLOTS.RANGE_START);
    } else if (position === "end") {
      this.setSlot(DATEPICKER_SLOTS.RANGE_END);
    }
  }
  getComparePosition() {
    const slot = this.activeSlot;
    if (slot === DATEPICKER_SLOTS.COMPARE_START) return "start";
    if (slot === DATEPICKER_SLOTS.COMPARE_END) return "end";
    return null;
  }
  setComparePosition(position) {
    if (position === "start") {
      this.setSlot(DATEPICKER_SLOTS.COMPARE_START);
    } else if (position === "end") {
      this.setSlot(DATEPICKER_SLOTS.COMPARE_END);
    }
  }
  reset({
    rangeTitle,
    compareTitle,
    range,
    compare
  }) {
    this.rangeType = rangeTitle;
    this.compareType = compareTitle;
    this.range = new DatePickerPeriod(range.start, range.end);
    this.compare = new DatePickerPeriod(compare.start, compare.end);
    this._updateMode();
    this.setSlot(DATEPICKER_SLOTS.RANGE_START);
  }
  clickDate(date) {
    const mDate = moment(date);

    // Ignore future dates
    if (mDate.isAfter(moment(), "day")) return;
    this._handleRangeClick(mDate);
  }
  _handleRangeClick(date) {
    if (!this.range.isComplete) {
      return this._selectRange(date);
    }

    // range is complete
    if (this.compareType === DATE_PICKER_COMPARE_TITLES.CUSTOM) {
      return this._handleCustomCompare(date);
    }
    return this._switchToCustomRange(date);
  }

  // --- helpers -------------------------------------------------------------

  _selectRange(date) {
    this.range.applyClick(date);
    this.autoComputeCompare();
    this.advanceSlot();
  }
  _selectCompare(date) {
    // Validate: compare dates must be strictly before range.start
    if (date.isSameOrAfter(this.range.start, "day")) {
      return; // Invalid selection, ignore
    }
    this.compare.applyClick(date);
    this.advanceSlot();
  }
  _handleCustomCompare(date) {
    if (!this.compare.isComplete) {
      return this._selectCompare(date);
    }

    // both complete — reset and start new range selection
    this._resetAll();
    this.setSlot(DATEPICKER_SLOTS.RANGE_START);
    return this._selectRange(date);
  }
  _switchToCustomRange(date) {
    this.rangeType = DATE_PICKER_RANGE_TITLES.CUSTOM;
    this._updateMode();
    this.range.reset();
    this.compare.reset();
    this._selectRange(date);
  }
  _resetAll() {
    this.range.reset();
    this.compare.reset();
  }
  _handleCompareClick(date) {
    if (!this.range.start || date.isSameOrAfter(this.range.start, "day")) return;
    if (this.compareType !== DATE_PICKER_COMPARE_TITLES.CUSTOM) {
      this.compareType = DATE_PICKER_COMPARE_TITLES.CUSTOM;
      this._updateMode();
      this.compare.reset();
      this.rangeSelection = null;
    }
    const nextFocus = this.compare.applyClick(date);
    this.compareSelection = nextFocus;
  }
  selectRangeType(rangeType) {
    const prevRangeType = this.rangeType;
    if (prevRangeType !== rangeType) {
      this.selectCompareType(DATE_PICKER_COMPARE_TITLES.PRECEDING_PERIOD);
    }
    this.rangeType = rangeType;
    this._updateMode();
    if (rangeType === DATE_PICKER_RANGE_TITLES.CUSTOM) {
      this.range.reset();
      this.compare.reset();
      this.setSlot(DATEPICKER_SLOTS.RANGE_START);
    } else {
      const res = getDatePickerRangeCompare(this.compareType, rangeType, this.weekType);
      if (res) {
        const {
          range,
          compare
        } = res;
        this.range.start = range.start ? moment(range.start) : null;
        this.range.end = range.end ? moment(range.end) : null;
        this.compare.start = compare.start ? moment(compare.start) : null;
        this.compare.end = compare.end ? moment(compare.end) : null;
      }
      // Range is auto-filled, move to compare
      this.setSlot(DATEPICKER_SLOTS.COMPARE_START);
    }
  }
  selectCompareType(compareType) {
    this.compareType = compareType;
    this._updateMode();
    switch (compareType) {
      case DATE_PICKER_COMPARE_TITLES.CUSTOM:
        this.compare.reset();
        this.setSlot(DATEPICKER_SLOTS.COMPARE_START);
        break;
      case DATE_PICKER_COMPARE_TITLES.PRECEDING_PERIOD:
      case DATE_PICKER_COMPARE_TITLES.SAME_PERIOD_LY:
        if (this.rangeType !== DATE_PICKER_RANGE_TITLES.CUSTOM) {
          const res = getDatePickerRangeCompare(this.compareType, this.rangeType, this.weekType);
          if (res) {
            const {
              compare
            } = res;
            this.compare.start = compare.start ? moment(compare.start) : null;
            this.compare.end = compare.end ? moment(compare.end) : null;
          }
        } else {
          this.autoComputeCompare();
        }
        break;
    }
  }
  autoComputeCompare() {
    if (!this.range.isComplete) return;
    const computeFn = CUSTOM_COMPARE_FUNCTION[this.compareType];
    if (computeFn) {
      const {
        compare
      } = computeFn({
        ...this.range.toObject(),
        externalPeriod: this.selectedPeriod
      });
      this.compare.update(compare);
    }
  }
  _updateMode() {
    this.DATE_PICKER_MODE = this.rangeType === DATE_PICKER_RANGE_TITLES.CUSTOM || this.compareType === DATE_PICKER_COMPARE_TITLES.CUSTOM ? DATE_PICKER_MODES.CUSTOM_MODE : DATE_PICKER_MODES.READ_ONLY_MODE;
  }
  get disableOK() {
    return !(this.range.isComplete && this.compare.isComplete);
  }
  toJSON() {
    return {
      range: this.range.toObject(),
      compare: this.compare.toObject(),
      activeSlot: this.activeSlot,
      rangeType: this.rangeType,
      compareType: this.compareType,
      DATE_PICKER_MODE: this.DATE_PICKER_MODE,
      rangeIsComplete: this.range.isComplete,
      compareIsComplete: this.compare.isComplete
    };
  }
}